import { useCallback, useRef, useState } from 'react'

const TARGET_SAMPLE_RATE = 16000

type UseMicrophoneOptions = {
  onAudioChunk: (chunk: ArrayBuffer) => void
}

export const useMicrophone = ({ onAudioChunk }: UseMicrophoneOptions) => {
  const [isActive, setIsActive] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const streamRef = useRef<MediaStream | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const isMutedRef = useRef(false)
  const onAudioChunkRef = useRef(onAudioChunk)
  onAudioChunkRef.current = onAudioChunk

  const cleanup = useCallback(() => {
    processorRef.current?.disconnect()
    sourceRef.current?.disconnect()
    audioContextRef.current?.close()
    streamRef.current?.getTracks().forEach((track) => track.stop())

    processorRef.current = null
    sourceRef.current = null
    audioContextRef.current = null
    streamRef.current = null
  }, [])

  const start = useCallback(async () => {
    try {
      setError(null)
      cleanup()

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      })
      streamRef.current = stream

      const audioContext = new AudioContext()
      audioContextRef.current = audioContext

      if (audioContext.state === 'suspended') {
        await audioContext.resume()
      }

      const source = audioContext.createMediaStreamSource(stream)
      sourceRef.current = source

      const processor = audioContext.createScriptProcessor(1024, 1, 1)
      processorRef.current = processor

      processor.onaudioprocess = (event: AudioProcessingEvent) => {
        if (isMutedRef.current) return

        const pcmData = event.inputBuffer.getChannelData(0)
        const sampleRate = audioContext.sampleRate
        const ratio = sampleRate / TARGET_SAMPLE_RATE
        const outputLength = Math.floor(pcmData.length / ratio)
        const int16Data = new Int16Array(outputLength)

        for (let i = 0; i < outputLength; i++) {
          const srcIndex = Math.floor(i * ratio)
          int16Data[i] = Math.max(
            -32768,
            Math.min(32767, pcmData[srcIndex] * 32768)
          )
        }

        onAudioChunkRef.current(int16Data.buffer as ArrayBuffer)
      }

      source.connect(processor)
      // Mute the processor output so mic audio doesn't play through
      // speakers, but keep the connection so onaudioprocess fires.
      const muteNode = audioContext.createGain()
      muteNode.gain.value = 0
      processor.connect(muteNode)
      muteNode.connect(audioContext.destination)
      setIsActive(true)
    } catch (err) {
      const message =
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? 'Microphone access was denied. Please allow microphone access and try again.'
          : 'Failed to access microphone.'
      setError(message)
    }
  }, [cleanup])

  const stop = useCallback(() => {
    cleanup()
    setIsActive(false)
    setIsMuted(false)
    isMutedRef.current = false
  }, [cleanup])

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      isMutedRef.current = !prev
      return !prev
    })
  }, [])

  return { isActive, isMuted, error, start, stop, toggleMute }
}
